import React, { useState, useEffect } from 'react';
import { collection, getDocs, query, orderBy } from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from '../authContext';
import { WalletService } from '../utils/walletService';
import { notificationService } from '../utils/notificationService';
import BottomNav from '../BottomNav';
import './AdminEarningsPage.css';

const AdminEarningsPage = () => {
  const { currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [ideas, setIdeas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [selectedUser, setSelectedUser] = useState('');
  const [selectedIdea, setSelectedIdea] = useState('');
  const [amount, setAmount] = useState('');
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState('idea_reward');

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch users
        const usersQuery = query(collection(db, 'users'), orderBy('name'));
        const usersSnapshot = await getDocs(usersQuery);
        const usersList = [];
        usersSnapshot.forEach((doc) => {
          const userData = doc.data();
          if (userData.role !== 'admin') { // Don't show admin users
            usersList.push({
              id: doc.id,
              ...userData
            });
          }
        });
        setUsers(usersList);

        // Fetch ideas/posts (assuming there's an ideas collection)
        try {
          const ideasQuery = query(collection(db, 'ideas'), orderBy('createdAt', 'desc'));
          const ideasSnapshot = await getDocs(ideasQuery);
          const ideasList = [];
          ideasSnapshot.forEach((doc) => {
            ideasList.push({
              id: doc.id,
              ...doc.data()
            });
          });
          setIdeas(ideasList);
        } catch (error) {
          console.log('Ideas collection not found, that\'s ok');
          setIdeas([]);
        }

      } catch (error) {
        console.error('Error fetching data:', error);
        notificationService.error('Error', 'Failed to load data');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const handleAddEarning = async (e) => {
    e.preventDefault();
    
    if (!selectedUser || !amount || !description) {
      notificationService.warning('Missing Information', 'Please fill in all required fields');
      return;
    }

    const earningAmount = parseFloat(amount);
    if (isNaN(earningAmount) || earningAmount <= 0) {
      notificationService.error('Invalid Amount', 'Please enter a valid positive amount');
      return;
    }

    setSubmitting(true);

    try {
      // Build description with idea reference if selected
      let fullDescription = description;
      if (selectedIdea) {
        const selectedIdeaData = ideas.find(idea => idea.id === selectedIdea);
        if (selectedIdeaData) {
          fullDescription += ` - Idea: "${selectedIdeaData.title || selectedIdeaData.description || 'Untitled'}"`;
        }
      }

      // Add balance to user's wallet
      const transactionId = await WalletService.addBalance(
        selectedUser,
        earningAmount,
        fullDescription,
        category
      );

      notificationService.success(
        'Earning Added Successfully! 💰',
        `₹${earningAmount} has been added to the user's wallet. Transaction ID: ${transactionId.slice(-6)}`
      );

      // Reset form
      setSelectedUser('');
      setSelectedIdea('');
      setAmount('');
      setDescription('');
      setCategory('idea_reward');

    } catch (error) {
      console.error('Error adding earning:', error);
      notificationService.error('Error', 'Failed to add earning: ' + error.message);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="admin-earnings-page">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading users and ideas...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-earnings-page">
      <div className="page-header">
        <h1>Add User Earnings</h1>
        <p>Add rewards to user wallets for ideas, competitions, and bonuses</p>
      </div>

      <div className="earnings-form-container">
        <form className="earnings-form" onSubmit={handleAddEarning}>
          <div className="form-group">
            <label htmlFor="user-select">Select User *</label>
            <select
              id="user-select"
              value={selectedUser}
              onChange={(e) => setSelectedUser(e.target.value)}
              required
              disabled={submitting}
            >
              <option value="">Choose a user...</option>
              {users.map(user => (
                <option key={user.id} value={user.id}>
                  {user.name || user.email} ({user.email})
                </option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label htmlFor="category-select">Earning Category *</label>
            <select
              id="category-select"
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              disabled={submitting}
            >
              <option value="idea_reward">Idea Reward</option>
              <option value="competition_reward">Competition Reward</option>
              <option value="bonus">Bonus</option>
              <option value="incentive">Performance Incentive</option>
            </select>
          </div>

          {ideas.length > 0 && (
            <div className="form-group">
              <label htmlFor="idea-select">Related Idea (Optional)</label>
              <select
                id="idea-select"
                value={selectedIdea}
                onChange={(e) => setSelectedIdea(e.target.value)}
                disabled={submitting}
              >
                <option value="">No specific idea...</option>
                {ideas.map(idea => (
                  <option key={idea.id} value={idea.id}>
                    {idea.title || idea.description?.substring(0, 50) || 'Untitled Idea'}
                    {idea.title && idea.description && '...'}
                  </option>
                ))}
              </select>
            </div>
          )}

          <div className="form-group">
            <label htmlFor="amount-input">Amount (₹) *</label>
            <input
              id="amount-input"
              type="number"
              min="1"
              step="0.01"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder="Enter amount (e.g., 500)"
              required
              disabled={submitting}
            />
          </div>

          <div className="form-group">
            <label htmlFor="description-input">Description *</label>
            <textarea
              id="description-input"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Enter earning description (e.g., 'Reward for innovative water management solution')"
              rows={3}
              required
              disabled={submitting}
            />
          </div>

          <button 
            type="submit" 
            className="submit-button"
            disabled={submitting}
          >
            {submitting ? (
              <>
                <span className="loading-spinner small"></span>
                Adding Earning...
              </>
            ) : (
              <>
                💰 Add Earning
              </>
            )}
          </button>
        </form>

        <div className="earning-info">
          <h3>💡 Information</h3>
          <ul>
            <li><strong>Idea Reward:</strong> Payment for submitted ideas</li>
            <li><strong>Competition Reward:</strong> Prize money for competition winners</li>
            <li><strong>Bonus:</strong> Additional rewards and incentives</li>
            <li><strong>Performance Incentive:</strong> Rewards for exceptional performance</li>
          </ul>
          
          <div className="earning-note">
            <p><strong>Note:</strong> Earnings will be immediately added to the user's wallet balance and will appear in their transaction history.</p>
          </div>
        </div>
      </div>
      <BottomNav />
    </div>
  );
};

export default AdminEarningsPage;